import React from 'react';
import styled from '@emotion/styled';
import { Icon } from '../icons';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  label?: string,
  name: string,
  onChange: Function,
  value: string,
  checked?: boolean,
  id?: string,
  size: 'small' | 'large',
  showOnlySelected?: boolean,
  withBorder?: boolean,
  customLabel?: React.Node,
};

export const Radio = ({
  label,
  customLabel,
  name,
  onChange,
  value,
  checked,
  id,
  size,
  showOnlySelected,
  withBorder,
  ...rest
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Radio);

  return (
    <>
      <RadioInput
        id={id || `radio-${name}-${value}`}
        type="radio"
        name={name}
        onChange={onChange}
        value={value}
        checked={checked}
        {...rest}
      />
      <StyledLabel
        role="radio"
        aria-checked={checked}
        tabIndex={0}
        htmlFor={id || `radio-${name}-${value}`}
        data-test={`radio-${name}-${value}`}
        size={size}
        withBorder={withBorder}
        componentTheme={componentTheme}
      >
        {customLabel || (
          <DefaultLabel showOnlySelected={showOnlySelected} label={label} componentTheme={componentTheme} />
        )}
      </StyledLabel>
    </>
  );
};

const DefaultLabel = ({
  showOnlySelected,
  label,
  componentTheme,
}: {
  showOnlySelected: boolean,
  label: string,
  componentTheme: any,
}) => (
  <>
    <SelectedIcon name="CheckCircle" componentTheme={componentTheme?.selected} />
    {!showOnlySelected && <UnSelectedIcon name="Circle" componentTheme={componentTheme?.unselected} />}
    {label && <LabelText>{label}</LabelText>}
  </>
);

const sizeStyles = {
  small: '20px',
  large: '38px',
};

const SelectedIcon = styled(Icon)`
  display: none;
  fill: ${({ theme, componentTheme }) => componentTheme?.iconColor || theme.color.brand};
`;

const UnSelectedIcon = styled(Icon)`
  fill: ${({ theme, componentTheme }) => componentTheme?.iconColor || theme.color.grey};
`;

const StyledLabel = styled('label')`
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSize.md};
  transition: ${({ theme }) => theme.motion.transition};

  ${({ withBorder, theme, active }) =>
    withBorder &&
    `
    padding: 10px 12px;
    border: ${theme.stroke.light};
    border-radius: ${theme.borderRadius.medium};
    ${active && `color: ${theme.color.brand};`}
  `};

  &:hover {
    svg {
      fill: ${({ theme, componentTheme }) => componentTheme?.hover?.iconColor || theme.color.brand};
    }
  }

  svg {
    font-size: ${({ size }) => sizeStyles[size]};
  }
`;

const LabelText = styled('span')`
  margin-left: 8px;
  display: flex;
  align-items: center;
`;

const RadioInput = styled('input')`
  display: none;

  &:checked + ${StyledLabel} {
    ${SelectedIcon} {
      display: inherit;
    }
    ${UnSelectedIcon} {
      display: none;
    }
  }
`;

Radio.defaultProps = {
  size: 'small',
};
